import { Link, useLocation, useHref } from "react-router-dom";
import { useLanguage, Language } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ChevronDown,
  Globe,
  LayoutDashboard,
  Menu,
  X,
  LogIn,
  LogOut,
  User,
  FileText,
  UserCircle,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.png";

const languages: { code: Language; label: string; nativeLabel: string }[] = [
  { code: "en", label: "English", nativeLabel: "English" },
  { code: "hi", label: "Hindi", nativeLabel: "हिंदी" },
  { code: "gu", label: "Gujarati", nativeLabel: "ગુજરાતી" },
];

const fullPageAuthPaths =
  /^\/(admin|staff-login|forgot-password|staff-forgot-password)(\/|$)/;

export function Header() {
  const { language, setLanguage, t } = useLanguage();
  const {
    isContributorAuthenticated,
    signOutContributor,
    canAccessStaffDashboard,
  } = useAuth();
  const location = useLocation();
  const adminHref = useHref("/admin");
  const staffLoginHref = useHref("/staff-login");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const currentLang = languages.find(l => l.code === language);

  if (
    location.pathname.startsWith("/admin") ||
    fullPageAuthPaths.test(location.pathname)
  ) {
    return null;
  }

  const navLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/browse", label: t("nav.browse") },
    { to: "/contribute", label: t("nav.contribute") },
  ];

  const isActive = (path: string) => location.pathname === path;

  const goAdminOrStaffLogin = () => {
    const origin = window.location.origin;
    const adminPath = adminHref.startsWith("/") ? adminHref : `/${adminHref}`;
    const staffPath = staffLoginHref.startsWith("/")
      ? staffLoginHref
      : `/${staffLoginHref}`;
    window.location.href = canAccessStaffDashboard
      ? `${origin}${adminPath}`
      : `${origin}${staffPath}`;
  };

  const handleLogout = async () => {
    try {
      await signOutContributor();
    } catch (error) {
      console.error("Logout error:", error);
    }
    window.location.replace("/");
  };

  /** Icon-only controls on very narrow viewports — fixed 40×40 */
  const sqIconBtn =
    "inline-flex h-10 w-10 shrink-0 items-center justify-center gap-0 p-0";
  const labeledBtnH = "h-10 min-h-10 shrink-0";

  const accountMenuItems = () => (
    <>
      <DropdownMenuItem asChild>
        <Link to="/account" className="cursor-pointer">
          <UserCircle className="mr-2 h-4 w-4" />
          {t("header.menuMyAccount")}
        </Link>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={() => void handleLogout()}
        className="text-destructive cursor-pointer"
      >
        <LogOut className="mr-2 h-4 w-4" />
        {t("header.logout")}
      </DropdownMenuItem>
    </>
  );

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full border-b",
        "border-border/50 bg-white"
      )}
    >
      <div
        className={cn(
          "container flex min-h-16 items-center justify-between gap-3 px-3 py-2 max-[499px]:gap-2 sm:px-4",
          "lg:grid lg:grid-cols-3 lg:items-center lg:gap-6 xl:gap-8"
        )}
      >
        <div className="flex min-w-0 shrink-0 justify-start pr-2 min-[500px]:pr-4">
          <Link
            to="/"
            className="flex min-w-0 items-center rounded-md bg-primary px-2 py-1 min-[500px]:px-2.5 min-[500px]:py-1.5"
            aria-label="Jain Lexicon"
          >
            <img
              src={logo}
              alt=""
              className="h-6 w-auto max-h-7 max-w-[min(130px,40vw)] object-contain object-left min-[500px]:h-7 min-[500px]:max-h-8 min-[500px]:max-w-none lg:h-8 lg:max-h-9"
              aria-hidden
            />
          </Link>
        </div>

        <nav
          className={cn(
            "hidden min-w-0 whitespace-nowrap lg:flex",
            "w-full flex-row items-center justify-center gap-5 xl:gap-10"
          )}
          aria-label={t("header.mainNav")}
        >
          {navLinks.map(link => {
            const active = isActive(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  "relative shrink-0 py-2 text-md font-semibold transition-colors xl:text-lg",
                  active
                    ? "text-primary"
                    : "text-foreground/85 hover:text-foreground"
                )}
              >
                {link.label}
                {active && (
                  <span
                    aria-hidden
                    className="bg-primary absolute inset-x-0 bottom-1 h-[3px] rounded-full"
                  />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="flex min-w-0 shrink-0 items-center justify-end gap-2 max-[499px]:gap-2 min-[500px]:gap-2.5 lg:gap-3">
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "text-foreground border-border/80 hover:bg-muted/70 text-sm font-medium shadow-sm",
                  sqIconBtn,
                  "max-[499px]:rounded-md",
                  "min-[500px]:h-10 min-[500px]:w-auto min-[500px]:gap-1.5 min-[500px]:rounded-md min-[500px]:px-3"
                )}
                aria-label={`${t("header.language")}: ${currentLang?.nativeLabel ?? ""}`}
              >
                <Globe className="h-[1.125rem] w-[1.125rem] shrink-0" />
                <span className="hidden max-w-[5.5rem] truncate font-medium min-[500px]:inline md:max-w-none md:text-base">
                  {currentLang?.nativeLabel}
                </span>
                <ChevronDown className="text-muted-foreground hidden h-4 w-4 shrink-0 opacity-70 min-[500px]:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {languages.map(lang => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={cn(
                    "flex cursor-pointer justify-between gap-4",
                    language === lang.code && "bg-secondary"
                  )}
                >
                  <span className="font-medium">{lang.label}</span>
                  <span className="text-muted-foreground text-xs">
                    {lang.nativeLabel}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="hidden min-[500px]:flex min-[500px]:items-center min-[500px]:gap-2.5">
            {!isContributorAuthenticated ? (
              <Link to="/login">
                <Button
                  variant="outline"
                  className={cn(
                    labeledBtnH,
                    "text-foreground border-border gap-2 px-3 font-medium hover:bg-muted/60"
                  )}
                  aria-label={t("header.login")}
                >
                  <LogIn className="h-4 w-4 shrink-0" />
                  <span>{t("header.login")}</span>
                </Button>
              </Link>
            ) : (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      labeledBtnH,
                      "text-foreground border-border hover:bg-muted/70 gap-1.5 rounded-full px-3 font-medium shadow-none lg:px-4"
                    )}
                    aria-label={t("header.myAccount")}
                  >
                    <User className="h-4 w-4 shrink-0" />
                    <span>{t("header.myAccount")}</span>
                    <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0 opacity-80" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {accountMenuItems()}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="default"
                  type="button"
                  className={cn(
                    labeledBtnH,
                    "gap-2 px-3 text-sm font-medium"
                  )}
                  onClick={goAdminOrStaffLogin}
                  aria-label={t("header.admin")}
                >
                  <LayoutDashboard className="h-4 w-4 shrink-0" />
                  <span>{t("header.admin")}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t("header.adminTooltip")}</TooltipContent>
            </Tooltip>
          </div>

          {!isContributorAuthenticated ? (
            <Link to="/login" className="min-[500px]:hidden">
              <Button
                variant="outline"
                className={cn(
                  sqIconBtn,
                  "rounded-md border-border text-foreground hover:bg-muted/60"
                )}
                aria-label={t("header.login")}
              >
                <LogIn className="h-4 w-4 shrink-0" />
              </Button>
            </Link>
          ) : (
            <div className="min-[500px]:hidden">
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      sqIconBtn,
                      "rounded-md border-border text-foreground hover:bg-muted/70"
                    )}
                    aria-label={t("header.myAccount")}
                  >
                    <User className="h-4 w-4 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {accountMenuItems()}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          <Button
            variant="ghost"
            size="icon"
            className={cn(sqIconBtn, "rounded-md lg:hidden")}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label={
              mobileMenuOpen ? t("header.closeMenu") : t("header.openMenu")
            }
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5 shrink-0" />
            ) : (
              <Menu className="h-5 w-5 shrink-0" />
            )}
          </Button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="animate-fade-in border-border/50 border-t bg-white lg:hidden">
          <nav className="container flex flex-col gap-2 py-4">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "rounded-lg px-4 py-3 text-md font-semibold",
                  isActive(link.to)
                    ? "bg-primary/10 text-primary"
                    : "text-foreground hover:bg-secondary hover:text-primary"
                )}
              >
                {link.label}
              </Link>
            ))}
            {isContributorAuthenticated && (
              <>
                <Link
                  to="/account"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-foreground hover:bg-secondary hover:text-primary flex items-center gap-2 rounded-lg px-4 py-3 text-xl font-semibold"
                >
                  <UserCircle className="h-5 w-5" />
                  {t("header.menuMyAccount")}
                </Link>
                <Link
                  to="/contribute#contributions"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-foreground hover:bg-secondary hover:text-primary flex items-center gap-2 rounded-lg px-4 py-3 text-xl font-semibold"
                >
                  <FileText className="h-4 w-4" />
                  {t("header.myContributions")}
                </Link>
              </>
            )}
            <button
              type="button"
              className={cn(
                "border-border/60 text-foreground hover:bg-secondary hover:text-primary mt-1 flex items-center gap-2 rounded-lg border border-dashed px-4 py-3 text-left text-md md:text-lg font-semibold",
                "max-[499px]:flex min-[500px]:hidden"
              )}
              onClick={() => {
                setMobileMenuOpen(false);
                goAdminOrStaffLogin();
              }}
            >
              <LayoutDashboard className="text-primary h-5 w-5 shrink-0" />
              {t("header.adminPortal")}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
